import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/lib/i18n";
import { NotificationBell } from "@/components/NotificationBell";
import { toast } from "sonner";
import { MapPin, Calendar, Ruler, Menu, User, FileText, Send, CheckCircle2, ArrowLeft, Filter, Lock, Unlock, RotateCcw, ChevronDown, ChevronUp, Bell, Building2, Settings, AlertTriangle, Save, Package, Globe, Copy, ExternalLink, Mail, Crown, EyeOff, Eye } from "lucide-react";
import { MaterialContactsManager } from "@/components/MaterialContactsManager";
import Logo from "@/components/Logo";
import { AppSidebar } from "@/components/AppSidebar";
import { useProjects } from "@/hooks/useProjects";
import { LocationRadiusPicker, type LocationRadius } from "@/components/LocationRadiusPicker";
import { MonthPicker } from "@/components/MonthPicker";
import { FACADE_SERVICE_LABEL, isFacadeServiceSlug, normalizeServiceSlugs, resolveSlug } from "@/config/serviceCatalog";
import { FeatureGate } from "@/components/FeatureGate";
import { FEATURE_KEYS } from "@/lib/featureCatalog";
import { useSupplierEntitlements } from "@/hooks/useSupplierEntitlements";
import { hasCompletedSupplierInfo, hasConfiguredPricing, isSupplierChatbotReady } from "@/lib/supplierReadiness";

interface Supplier {
  id: string;
  company_name: string;
  contact_email: string | null;
  contact_phone: string | null;
  region: string | null;
  ico: string | null;
  description: string | null;
  slug: string | null;
  custom_domain: string | null;
  enabled_services: string[];
}

interface Inquiry {
  id: string;
  title: string;
  description: string | null;
  region: string | null;
  status: string;
  photos: string[] | null;
  created_at: string;
  lat: number | null;
  lng: number | null;
  engine_id: string | null;
  source: string | null;
}

export default function SupplierDashboard() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { sites, chats, createChat, deleteChat, assignChat, addSite } = useProjects();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setSidebarOpen(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const [submittedQuotes, setSubmittedQuotes] = useState<Set<string>>(new Set());
  const [quoteForm, setQuoteForm] = useState({ total_price: "", notes: "" });
  const [savingQuote, setSavingQuote] = useState(false);
  const [approvedQuotes, setApprovedQuotes] = useState<Set<string>>(new Set());
  const [hasPriceItems, setHasPriceItems] = useState(false);
  const [editingCompany, setEditingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({ company_name: "", contact_email: "", contact_phone: "", ico: "", description: "", region: "" });
  const [savingCompany, setSavingCompany] = useState(false);
  const [materialSent, setMaterialSent] = useState<Set<string>>(new Set());
  const [dnsOpen, setDnsOpen] = useState(false);
  const [customerMessages, setCustomerMessages] = useState<{ id: string; customer_email: string; message: string; is_read: boolean; created_at: string; lang: string | null }[]>([]);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [translatedMessages, setTranslatedMessages] = useState<Record<string, string>>({});
  const [translatingIds, setTranslatingIds] = useState<Set<string>>(new Set());
  const [copiedSlug, setCopiedSlug] = useState(false);
  const [editingSlug, setEditingSlug] = useState(false);
  const [slugInput, setSlugInput] = useState("");
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [savingSlug, setSavingSlug] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [domainStatus, setDomainStatus] = useState<"idle" | "loading" | "error">("idle");
  const [domainInfo, setDomainInfo] = useState<{ verified?: boolean; verification?: Array<{ type: string; domain: string; value: string }> | null; error?: string } | null>(null);

  const toSlug = (name: string) =>
    name
      .toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+(s\.r\.o\.|a\.s\.|spol\.|s\.p\.|v\.o\.s\.)/gi, "")
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

  const checkSlug = async (value: string) => {
    if (!value || value.length < 2) { setSlugAvailable(null); return; }
    setCheckingSlug(true);
    const { data } = await supabase.rpc("check_slug_available", {
      p_slug: value,
      p_supplier_id: supplier?.id ?? null,
    });
    setSlugAvailable(data as boolean);
    setCheckingSlug(false);
  };

  const saveSlug = async () => {
    if (!supplier || !slugInput || slugAvailable === false) return;
    setSavingSlug(true);
    const { error } = await supabase
      .from("suppliers")
      .update({ slug: slugInput })
      .eq("id", supplier.id);
    if (!error) {
      setSupplier({ ...supplier, slug: slugInput });
      setEditingSlug(false);
      toast.success(t("supplier.slugSaved"));
    } else {
      toast.error(t("supplier.slugTakenOrInvalid"));
    }
    setSavingSlug(false);
  };

  const callDomainApi = async (action: string, domain?: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL || "https://emnmfwplgwwjauxytixr.supabase.co"}/functions/v1/manage-domain`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action, domain }),
      }
    );
    return res.json();
  };

  const addDomain = async () => {
    if (!domainInput.trim()) return;
    setDomainStatus("loading");
    try {
      const result = await callDomainApi("add", domainInput.trim());
      if (result?.error) {
        setDomainInfo({ error: result.error });
        setDomainStatus("error");
      } else {
        setDomainInfo(result);
        setDomainStatus("idle");
        if (supplier) setSupplier({ ...supplier, custom_domain: result.domain });
      }
    } catch {
      setDomainInfo({ error: t("supplier.connectionError") });
      setDomainStatus("error");
    }
  };

  const checkDomain = async () => {
    setDomainStatus("loading");
    try {
      const result = await callDomainApi("check");
      setDomainInfo(result);
      setDomainStatus("idle");
    } catch {
      setDomainInfo({ error: t("supplier.connectionError") });
      setDomainStatus("error");
    }
  };

  const removeDomain = async () => {
    setDomainStatus("loading");
    try {
      const result = await callDomainApi("remove");
      if (!result?.error) {
        if (supplier) setSupplier({ ...supplier, custom_domain: null });
        setDomainInfo(null);
        setDomainInput("");
      }
      setDomainStatus("idle");
    } catch {
      setDomainStatus("error");
    }
  };

  // Filters
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filtersLocked, setFiltersLocked] = useState(false);
  const [filterWorkType, setFilterWorkType] = useState("all");
  const [filterLocations, setFilterLocations] = useState<LocationRadius[]>([]);
  const [filterLocationsDirty, setFilterLocationsDirty] = useState(false);
  const [filterPriceFrom, setFilterPriceFrom] = useState("");
  const [filterPriceTo, setFilterPriceTo] = useState("");
  const [filterDeadlinePreset, setFilterDeadlinePreset] = useState("");
  const [sourceTab, setSourceTab] = useState<"chatbot" | "marketplace">("chatbot");
  const [chatbotFilter, setChatbotFilter] = useState<"novy" | "nabidka" | "skryto">("novy");
  const [hiddenInquiries, setHiddenInquiries] = useState<Set<string>>(new Set());

  // Entitlement check for marketplace inquiries
  const { canUse: canUseFeature } = useSupplierEntitlements(supplier?.id);
  const hasMarketplace = canUseFeature(FEATURE_KEYS.MARKETPLACE_INQUIRIES);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (searchParams.get("messages") !== "1") return;
    setMessagesOpen(true);

    const url = new URL(window.location.href);
    url.searchParams.delete("messages");
    window.history.replaceState({}, "", url.toString());
  }, [searchParams]);

  // Handle payment success - verify and mark quote as paid
  useEffect(() => {
    const verifyPayment = async () => {
      const payment = searchParams.get("payment");
      const inquiryId = searchParams.get("inquiry");
      if (payment !== "success" || !inquiryId || !supplier) return;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const resp = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-payment`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ inquiry_id: inquiryId }),
          }
        );
        const result = await resp.json();

        if (result.verified) {
          toast.success(t("supplier.quoteSentSuccess"));
          setSubmittedQuotes((prev) => new Set([...prev, inquiryId]));
          // Show the inquiry detail
          const matchedInquiry = inquiries.find((i) => i.id === inquiryId);
          if (matchedInquiry) setSelectedInquiry(matchedInquiry);
        } else {
          toast.error(t("supplier.paymentNotVerified"));
        }
      } catch (err) {
        console.error("Payment verification error:", err);
        toast.error(t("supplier.paymentVerificationError"));
      }

      const url = new URL(window.location.href);
      url.searchParams.delete("payment");
      url.searchParams.delete("inquiry");
      window.history.replaceState({}, "", url.toString());
    };

    verifyPayment();
  }, [searchParams, supplier, inquiries]);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/supplier/register");
      return;
    }

    const { data: supplierData } = await supabase
      .from("suppliers")
      .select("*")
      .eq("user_id", session.user.id)
      .single();

    if (!supplierData) {
      navigate("/supplier/register");
      return;
    }

    const normalizedSupplierData = {
      ...supplierData,
      enabled_services: normalizeServiceSlugs(supplierData.enabled_services || []),
      filter_work_type: supplierData.filter_work_type ? resolveSlug(supplierData.filter_work_type) : "all",
    };
    setSupplier(normalizedSupplierData);
    setCompanyForm({
      company_name: normalizedSupplierData.company_name || "",
      contact_email: normalizedSupplierData.contact_email || "",
      contact_phone: normalizedSupplierData.contact_phone || "",
      ico: normalizedSupplierData.ico || "",
      description: normalizedSupplierData.description || "",
      region: normalizedSupplierData.region || "",
    });
    // Restore persisted filter state
    setFiltersLocked(normalizedSupplierData.filter_locked ?? false);
    setFilterWorkType(normalizedSupplierData.filter_work_type ?? "all");
    setFilterPriceFrom(normalizedSupplierData.filter_price_from ?? "");
    setFilterPriceTo(normalizedSupplierData.filter_price_to ?? "");
    setFilterDeadlinePreset(normalizedSupplierData.filter_deadline_preset ?? "");
    // Load hidden chatbot inquiries from localStorage
    try {
      const stored = localStorage.getItem(`hidden_inquiries_${normalizedSupplierData.id}`);
      if (stored) setHiddenInquiries(new Set(JSON.parse(stored)));
    } catch { /* ignore parse errors */ }
    await Promise.all([
      loadInquiries(),
      loadSubmittedQuotes(normalizedSupplierData.id),
      loadApprovedQuotes(normalizedSupplierData.id),
      loadLocationPreferences(normalizedSupplierData.id),
      loadPriceItemsCount(normalizedSupplierData.id),
      loadCustomerMessages(normalizedSupplierData.id),
    ]);
    setLoading(false);
  };

  const loadInquiries = async () => {
    const { data } = await supabase
      .from("inquiries")
      .select("id, title, description, region, status, photos, created_at, lat, lng, engine_id, source")
      .eq("status", "open")
      .order("created_at", { ascending: false });
    setInquiries(data || []);
  };

  const loadSubmittedQuotes = async (supplierId: string) => {
    const { data } = await supabase
      .from("supplier_quotes")
      .select("inquiry_id")
      .eq("supplier_id", supplierId);
    setSubmittedQuotes(new Set((data || []).map((q: { inquiry_id: string }) => q.inquiry_id)));
  };

  const loadApprovedQuotes = async (supplierId: string) => {
    const { data } = await supabase
      .from("supplier_quotes")
      .select("inquiry_id, customer_approved")
      .eq("supplier_id", supplierId)
      .eq("customer_approved", true);
    setApprovedQuotes(new Set((data || []).map((q: { inquiry_id: string }) => q.inquiry_id)));
  };

  const loadLocationPreferences = async (supplierId: string) => {
    const { data } = await supabase
      .from("supplier_location_preferences")
      .select("*")
      .eq("supplier_id", supplierId);
    if (data && data.length > 0) {
      setFilterLocations(
        data.map((d: any) => ({
          id: d.id,
          label: d.label,
          lat: d.lat,
          lng: d.lng,
          radius_km: d.radius_km,
          is_whole_country: d.is_whole_country,
        }))
      );
    }
  };

  const loadPriceItemsCount = async (supplierId: string) => {
    const { count } = await supabase
      .from("supplier_price_items")
      .select("id", { count: "exact", head: true })
      .eq("supplier_id", supplierId)
      .eq("is_active", true);
    setHasPriceItems((count ?? 0) > 0);
  };

  const loadCustomerMessages = async (supplierId: string) => {
    const { data } = await supabase
      .from("supplier_customer_messages")
      .select("id, customer_email, message, is_read, created_at, lang")
      .eq("supplier_id", supplierId)
      .order("created_at", { ascending: false })
      .limit(20);
    setCustomerMessages(data || []);
  };

  const markMessageRead = async (msgId: string) => {
    await supabase.from("supplier_customer_messages").update({ is_read: true }).eq("id", msgId);
    setCustomerMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, is_read: true } : m));
  };

  const translateMessage = async (msgId: string, text: string, sourceLang: string) => {
    if (translatedMessages[msgId] || translatingIds.has(msgId)) return;
    setTranslatingIds((prev) => new Set(prev).add(msgId));
    try {
      const { data, error } = await supabase.functions.invoke("translate-message", {
        body: { text, source_lang: sourceLang, target_lang: lang },
      });
      if (!error && data?.translated_text) {
        setTranslatedMessages((prev) => ({ ...prev, [msgId]: data.translated_text }));
      }
    } catch (e) {
      console.error("Translation failed:", e);
    } finally {
      setTranslatingIds((prev) => { const s = new Set(prev); s.delete(msgId); return s; });
    }
  };

  const saveCompanyInfo = async () => {
    if (!supplier) return;
    setSavingCompany(true);
    const { error } = await supabase
      .from("suppliers")
      .update({
        company_name: companyForm.company_name,
        contact_email: companyForm.contact_email || null,
        contact_phone: companyForm.contact_phone || null,
        ico: companyForm.ico || null,
        description: companyForm.description || null,
        region: companyForm.region || null,
      })
      .eq("id", supplier.id);
    if (error) {
      toast.error(t("supplier.savingError"));
    } else {
      toast.success(t("supplier.dataSaved"));
      setSupplier({ ...supplier, ...companyForm });
      setEditingCompany(false);
    }
    setSavingCompany(false);
  };

  const saveLocationPreferences = async () => {
    if (!supplier) return;
    await supabase
      .from("supplier_location_preferences")
      .delete()
      .eq("supplier_id", supplier.id);
    if (filterLocations.length > 0) {
      await supabase.from("supplier_location_preferences").insert(
        filterLocations.map((loc) => ({
          supplier_id: supplier.id,
          label: loc.label,
          lat: loc.lat,
          lng: loc.lng,
          radius_km: loc.radius_km,
          is_whole_country: loc.is_whole_country,
        }))
      );
    }
    setFilterLocationsDirty(false);
  };

  const saveFilterState = async (locked: boolean) => {
    if (!supplier) return;
    await supabase
      .from("suppliers")
      .update({
        filter_locked: locked,
        filter_work_type: filterWorkType,
        filter_price_from: filterPriceFrom || null,
        filter_price_to: filterPriceTo || null,
        filter_deadline_preset: filterDeadlinePreset || null,
      })
      .eq("id", supplier.id);
  };

  const handleSubmitQuoteAndPay = async (inquiryId: string) => {
    if (!supplier || !quoteForm.total_price) {
      toast.error(t("supplier.fillQuotePrice"));
      return;
    }
    setSavingQuote(true);

    const inquiry = inquiries.find((i) => i.id === inquiryId);

    // Save the quote
    const { error } = await supabase.from("supplier_quotes").insert({
      supplier_id: supplier.id,
      inquiry_id: inquiryId,
      total_price: parseFloat(quoteForm.total_price),
      region: inquiry?.region || null,
      notes: quoteForm.notes || null,
    });

    if (error) {
      setSavingQuote(false);
      if ((error as { code?: string }).code === "23505") {
        toast.info(t("supplier.quoteAlreadySent"));
      } else {
        toast.error(error.message);
      }
      return;
    }

    // Notify customer via email
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-customer-quote`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              inquiry_id: inquiryId,
              total_price: parseFloat(quoteForm.total_price),
            }),
          }
        );
      }
    } catch (notifyErr) {
      console.error("Failed to notify customer:", notifyErr);
    }

    setQuoteForm({ total_price: "", notes: "" });
    setSavingQuote(false);

    // Redirect to Stripe payment
    setSelectedInquiry(null);
    await handlePayForQuote(inquiryId);
  };

  const handlePayForQuote = async (inquiryId: string) => {
    if (!supplier) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error(t("supplier.loginRequired"));
        return;
      }

      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            inquiry_id: inquiryId,
            success_url: `${window.location.origin}/supplier/dashboard?payment=success&inquiry=${inquiryId}`,
            cancel_url: `${window.location.origin}/supplier/dashboard?payment=cancelled`,
          }),
        }
      );

      const result = await resp.json();

      if (result.already_paid) {
        toast.info(t("supplier.alreadyPaid"));
        setSubmittedQuotes((prev) => new Set([...prev, inquiryId]));
        return;
      }

      if (result.url) {
        window.open(result.url, "_blank");
      } else {
        toast.error(result.error || t("supplier.paymentError"));
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error(t("supplier.paymentError"));
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/supplier/register");
  };

  const parseDescription = (desc: string | null): Record<string, string> => {
    if (!desc) return {};
    const result: Record<string, string> = {};
    for (const line of desc.split("\n")) {
      const idx = line.indexOf(":");
      if (idx > 0) {
        const key = line.slice(0, idx).trim();
        const val = line.slice(idx + 1).trim();
        if (val && val !== "null") result[key] = val;
      }
    }
    return result;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">...</div>
      </div>
    );
  }

  const toggleHideInquiry = (inquiryId: string) => {
    setHiddenInquiries((prev) => {
      const next = new Set(prev);
      if (next.has(inquiryId)) next.delete(inquiryId);
      else next.add(inquiryId);
      if (supplier) {
        localStorage.setItem(`hidden_inquiries_${supplier.id}`, JSON.stringify([...next]));
      }
      return next;
    });
  };

  const resetFilters = async () => {
    setFilterWorkType("all");
    setFilterLocations([]);
    setFilterLocationsDirty(true);
    setFilterPriceFrom("");
    setFilterPriceTo("");
    setFilterDeadlinePreset("");
    setFiltersLocked(false);
    // Persist reset
    if (supplier) {
      await Promise.all([
        supabase.from("suppliers").update({
          filter_locked: false,
          filter_work_type: "all",
          filter_price_from: null,
          filter_price_to: null,
          filter_deadline_preset: null,
        }).eq("id", supplier.id),
        supabase.from("supplier_location_preferences").delete().eq("supplier_id", supplier.id),
      ]);
    }
  };

  // Haversine distance in km
  const haversineDistance = (lat1: number, lng1: number, lat2: number, lng2: number) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const filteredInquiries = inquiries.filter((inq) => {
    const details = parseDescription(inq.description);

    // Location filter – use haversine distance when coordinates available
    if (filterLocations.length > 0) {
      const hasWhole = filterLocations.some((l) => l.is_whole_country);
      if (!hasWhole) {
        if (inq.lat && inq.lng) {
          // GPS-based matching
          const matchesAny = filterLocations.some((fl) => {
            const dist = haversineDistance(inq.lat!, inq.lng!, fl.lat, fl.lng);
            return dist <= fl.radius_km;
          });
          if (!matchesAny) return false;
        } else {
          // Fallback: text-based matching for old inquiries without coordinates
          const loc = (inq.region || "").toLowerCase() + " " + (details["Místo"] || details["Lokalita"] || "").toLowerCase();
          const matchesAny = filterLocations.some((fl) => loc.includes(fl.label.split(",")[0].toLowerCase()));
          if (!matchesAny) return false;
        }
      }
    }

    // Price filter
    const priceStr = details["Odhadovaná cena"] || details["Rozpočet"] || details["Budget"] || "";
    const priceNum = parseFloat(priceStr.replace(/[^\d]/g, ""));
    if (filterPriceFrom && priceNum && priceNum < parseFloat(filterPriceFrom)) return false;
    if (filterPriceTo && priceNum && priceNum > parseFloat(filterPriceTo)) return false;

    // Deadline filter - not yet matching against inquiry data, just a preference for notifications
    // Future: match against inquiry realization_month field

    // Service filter — supplier can filter by their enabled_services or show all
    if (filterWorkType !== "all") {
      if (resolveSlug(inq.engine_id || "") !== resolveSlug(filterWorkType)) return false;
    }

    return true;
  });

  const isQuoteSent = (id: string) => submittedQuotes.has(id);
  const isApproved = (id: string) => approvedQuotes.has(id);

  return (
      <div className="flex h-[100dvh] bg-background overflow-hidden">
      <AppSidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onClose={() => setSidebarOpen(false)}
        isLoggedIn={true}
        sites={sites}
        chats={chats}
        onNewChat={() => navigate("/")}
        onSelectChat={() => navigate("/")}
        onDeleteChat={async (id) => { await deleteChat(id); }}
        onAssignChat={async (chatId, siteId) => { await assignChat(chatId, siteId); }}
        onAddSite={async (name) => { await addSite(name); }}
      />

      <div className={`flex-1 flex flex-col h-full overflow-hidden transition-all duration-200 ${sidebarOpen ? "lg:ml-[260px]" : ""}`}>

      <header className="flex items-center justify-between px-4 h-12">
        <div className="flex items-center gap-0.5 sm:gap-2">
          <button
            onClick={() => setSidebarOpen(true)}
            className={`p-1.5 sm:p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground -ml-1 sm:ml-0 ${sidebarOpen ? "lg:hidden" : ""}`}
          >
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/" className="flex items-center gap-1 text-foreground text-base hover:opacity-70 transition-opacity">
            <Logo size={32} />
            <span className="font-semibold">cenik.ai</span>
          </Link>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <Link to="/profile" className="relative p-2 rounded-full hover:bg-accent transition-colors text-muted-foreground">
            <User className="w-5 h-5" />
          </Link>
          <NotificationBell />
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <div className="mb-4 flex items-center gap-2">
          <button onClick={() => navigate("/")} className="p-2 rounded-full hover:bg-accent transition-colors">
            <ArrowLeft className="w-4 h-4 text-foreground" />
          </button>
          <span className="font-semibold text-foreground text-sm">{supplier?.company_name}</span>
          <Link
            to="/supplier/settings"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors underline"
          >
            {t("supplier.edit")}
          </Link>
          <span className="text-muted-foreground text-xs ml-auto">{t("supplier.dashboard")}</span>
        </div>

        {/* Incomplete profile banner */}
        {supplier && supplier.company_name === supplier.contact_email && (
          <div className="mb-4 border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 rounded-xl px-4 py-3 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <div className="flex-1 text-sm text-amber-800 dark:text-amber-300">
              {t("supplier.incompleteProfile")}
            </div>
            <Link
              to="/supplier/settings"
              className="text-xs font-medium text-amber-700 dark:text-amber-300 hover:underline whitespace-nowrap"
            >
              {t("supplier.goToSettings")} →
            </Link>
          </div>
        )}


        {/* Vlastní AI chat kalkulačka */}
        {supplier && (() => {
          const activeSlug = supplier.slug || toSlug(supplier.company_name);
          const url = `cenik.ai/${activeSlug}`;
          const infoReady = hasCompletedSupplierInfo(supplier);
          const pricingReady = hasConfiguredPricing(hasPriceItems);
          const ready = infoReady && pricingReady;
          return (
            <div className="mb-4 border border-border rounded-xl bg-card overflow-hidden">
              <div className="px-4 py-3 flex items-start justify-between gap-4">
                {/* Left column: chatbot info */}
                <div className="flex items-start gap-2 min-w-0 flex-1">
                  <Globe className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-foreground">{t("supplier.aiChatCalculator")}</span>
                      <span className="text-[10px] font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full whitespace-nowrap">{t("supplier.freeTestVersion")}</span>
                      <a
                        href={`https://${url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-0.5 rounded hover:bg-accent transition-colors text-muted-foreground"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>

                    {/* Slug zobrazení / editace */}
                    {editingSlug ? (
                      <div className="mt-1.5 space-y-1">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground font-mono">cenik.ai/</span>
                          <input
                            autoFocus
                            value={slugInput}
                            onChange={(e) => {
                              const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
                              setSlugInput(val);
                              setSlugAvailable(null);
                            }}
                            onBlur={() => checkSlug(slugInput)}
                            className="flex-1 text-xs font-mono px-2 py-1 rounded-md border border-border bg-background outline-none focus:ring-1 focus:ring-ring"
                            placeholder={toSlug(supplier.company_name)}
                            maxLength={60}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          {checkingSlug && <span className="text-[10px] text-muted-foreground">{t("supplier.checking")}</span>}
                          {!checkingSlug && slugAvailable === true && <span className="text-[10px] text-green-600">{t("supplier.slugAvailableLabel")}</span>}
                          {!checkingSlug && slugAvailable === false && <span className="text-[10px] text-destructive">{t("supplier.slugTakenLabel")}</span>}
                          <button
                            onClick={saveSlug}
                            disabled={savingSlug || !slugInput || slugAvailable === false}
                            className="text-[10px] font-medium px-2 py-0.5 rounded-md bg-primary text-primary-foreground disabled:opacity-40"
                          >
                            {savingSlug ? t("supplier.savingLabel") : t("supplier.saveLabel")}
                          </button>
                          <button
                            onClick={() => { setEditingSlug(false); setSlugAvailable(null); }}
                            className="text-[10px] text-muted-foreground hover:text-foreground"
                          >
                            {t("supplier.cancel")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <a
                            href={`https://${url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline font-mono"
                          >
                            {url}
                          </a>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(`https://${url}`);
                              setCopiedSlug(true);
                              setTimeout(() => setCopiedSlug(false), 2000);
                            }}
                            className="p-0.5 rounded hover:bg-accent transition-colors text-muted-foreground"
                            title={t("supplier.copyLink")}
                          >
                            {copiedSlug ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            onClick={() => { setSlugInput(activeSlug); setEditingSlug(true); setSlugAvailable(null); }}
                            className="text-[10px] text-muted-foreground hover:text-foreground underline"
                          >
                            {t("supplier.edit")}
                          </button>
                        </div>
                        {supplier.custom_domain && (
                          <div className="flex items-center gap-1.5 mt-1">
                            <a
                              href={`https://${supplier.custom_domain}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline font-mono"
                            >
                              {supplier.custom_domain}
                            </a>
                            <span className="text-[10px] text-muted-foreground">{t("supplier.customDomainTag")}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Right column: readiness checklist + CTA */}
                {!editingSlug && (
                  <div className="shrink-0 w-[220px] space-y-2 pt-0.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        {infoReady
                          ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                          : <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                        }
                        <span className="text-xs text-foreground">{t("supplier.readiness.companyInfo")}</span>
                      </div>
                      <span className={`text-[10px] font-medium ${infoReady ? "text-green-600" : "text-amber-500"}`}>
                        {infoReady ? t("supplier.readiness.done") : t("supplier.readiness.missing")}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        {pricingReady
                          ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                          : <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                        }
                        <span className="text-xs text-foreground">{t("supplier.readiness.pricing")}</span>
                      </div>
                      <span className={`text-[10px] font-medium ${pricingReady ? "text-green-600" : "text-amber-500"}`}>
                        {pricingReady ? t("supplier.readiness.done") : t("supplier.readiness.missing")}
                      </span>
                    </div>
                    <Link
                      to="/supplier/settings"
                      className="flex items-center justify-center gap-1.5 w-full rounded-lg bg-primary text-primary-foreground px-3 py-2 text-xs font-semibold hover:opacity-90 transition-opacity"
                    >
                      <Settings className="w-3.5 h-3.5" />
                      {t("supplier.readiness.setupCta")}
                    </Link>
                  </div>
                )}
              </div>

              {/* Custom domain management — gated by custom_domain entitlement */}
              <FeatureGate featureKey={FEATURE_KEYS.CUSTOM_DOMAIN} supplierId={supplier?.id}>
              <div
                className="border-t border-border px-4 py-2.5 flex items-center justify-between cursor-pointer hover:bg-muted/40 transition-colors"
                onClick={() => setDnsOpen(!dnsOpen)}
              >
                <span className="text-xs text-muted-foreground">
                  {supplier.custom_domain ? t("supplier.customDomainLabel").replace("{domain}", supplier.custom_domain) : t("supplier.connectCustomDomain")}
                </span>
                {dnsOpen ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
              </div>

              {dnsOpen && (
                <div className="border-t border-border px-4 py-3 bg-muted/30 space-y-3 text-xs text-muted-foreground">
                  {!supplier.custom_domain ? (
                    <>
                      <p>{t("supplier.enterDomainLabel").replace("{example}", "printx.cz")}</p>
                      <div className="flex items-center gap-2">
                        <input
                          value={domainInput}
                          onChange={(e) => setDomainInput(e.target.value.toLowerCase().trim())}
                          placeholder="printx.cz"
                          className="flex-1 text-xs font-mono px-2 py-1.5 rounded-md border border-border bg-background outline-none focus:ring-1 focus:ring-ring"
                        />
                        <button
                          onClick={addDomain}
                          disabled={domainStatus === "loading" || !domainInput.trim()}
                          className="text-xs font-medium px-3 py-1.5 rounded-md bg-primary text-primary-foreground disabled:opacity-40"
                        >
                          {domainStatus === "loading" ? "..." : t("supplier.connectButton")}
                        </button>
                      </div>
                      {domainInfo?.error && (
                        <p className="text-destructive text-[10px]">{domainInfo.error}</p>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-foreground">{supplier.custom_domain}</span>
                        {domainInfo?.verified ? (
                          <span className="text-[10px] font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{t("supplier.verifiedLabel")}</span>
                        ) : (
                          <span className="text-[10px] font-semibold bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">{t("supplier.awaitingDns")}</span>
                        )}
                      </div>

                      {!domainInfo?.verified && (
                        <>
                          <p className="font-medium text-foreground text-[11px] mb-1">{t("supplier.dnsStep1Title")}</p>
                          <p>{t("supplier.dnsStep1Instruction")}</p>
                          <p className="text-[10px] mb-1">{t("supplier.mainDomainLabel").replace("{example}", "printx.cz")}</p>
                          <div className="rounded-lg border border-border bg-background overflow-hidden">
                            <div className="grid grid-cols-3 gap-0 text-[10px] font-medium text-muted-foreground bg-muted/50 px-3 py-1.5">
                              <span>{t("supplier.dnsTypeHeader")}</span><span>{t("supplier.dnsNameHeader")}</span><span>{t("supplier.dnsValueHeader")}</span>
                            </div>
                            <div className="grid grid-cols-3 gap-0 px-3 py-2 font-mono text-foreground">
                              <span>A</span><span>@</span><span>76.76.21.21</span>
                            </div>
                          </div>

                          <p className="text-[10px] mt-2 mb-1">{t("supplier.subdomainLabel").replace("{example}", "cenik.printx.cz")}</p>
                          <div className="rounded-lg border border-border bg-background overflow-hidden">
                            <div className="grid grid-cols-3 gap-0 text-[10px] font-medium text-muted-foreground bg-muted/50 px-3 py-1.5">
                              <span>{t("supplier.dnsTypeHeader")}</span><span>{t("supplier.dnsNameHeader")}</span><span>{t("supplier.dnsValueHeader")}</span>
                            </div>
                            <div className="grid grid-cols-3 gap-0 px-3 py-2 font-mono text-foreground">
                              <span>CNAME</span><span>cenik</span><span>cname.vercel-dns.com</span>
                            </div>
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-1">{t("supplier.subdomainHint").replace("{sub}", "cenik").replace("{ex1}", "chat").replace("{ex2}", "kalkulacka")}</p>

                          {domainInfo?.verification && domainInfo.verification.length > 0 && (
                            <>
                              <p className="font-medium text-foreground text-[11px] mt-2 mb-1">{t("supplier.dnsStep2Title")}</p>
                              <p>{t("supplier.dnsStep2Instruction")}</p>
                              <div className="rounded-lg border border-border bg-background overflow-hidden">
                                <div className="grid grid-cols-3 gap-0 text-[10px] font-medium text-muted-foreground bg-muted/50 px-3 py-1.5">
                                  <span>{t("supplier.dnsTypeHeader")}</span><span>{t("supplier.dnsNameHeader")}</span><span>{t("supplier.dnsValueHeader")}</span>
                                </div>
                                {domainInfo.verification.map((v, i) => (
                                  <div key={i} className="grid grid-cols-3 gap-0 px-3 py-2 font-mono text-foreground text-[11px]">
                                    <span>{v.type}</span><span>{v.domain}</span><span className="truncate">{v.value}</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}

                          <div className="mt-2 p-2.5 rounded-lg border border-border bg-muted/30 space-y-1.5">
                            <p className="font-medium text-foreground text-[11px]">{t("supplier.dnsSettingsTitle")}</p>
                            <ul className="space-y-1 text-[10px] leading-relaxed">
                              <li><span className="font-medium">Forpsi:</span> {t("supplier.dnsForpsi")}</li>
                              <li><span className="font-medium">Active24:</span> {t("supplier.dnsActive24")}</li>
                              <li><span className="font-medium">WEDOS:</span> {t("supplier.dnsWedos")}</li>
                              <li><span className="font-medium">Cloudflare:</span> {t("supplier.dnsCloudflare")}</li>
                            </ul>
                            <p className="text-[10px] text-muted-foreground mt-1">{t("supplier.dnsPropagationHint")}</p>
                          </div>
                        </>
                      )}

                      <div className="flex items-center gap-2">
                        <button
                          onClick={checkDomain}
                          disabled={domainStatus === "loading"}
                          className="text-[10px] font-medium px-2 py-1 rounded-md border border-border hover:bg-accent"
                        >
                          {domainStatus === "loading" ? "..." : t("supplier.verifyDns")}
                        </button>
                        <button
                          onClick={removeDomain}
                          disabled={domainStatus === "loading"}
                          className="text-[10px] font-medium px-2 py-1 rounded-md text-destructive hover:bg-destructive/10"
                        >
                          {t("supplier.disconnectDomain")}
                        </button>
                      </div>
                      {domainInfo?.error && (
                        <p className="text-destructive text-[10px]">{domainInfo.error}</p>
                      )}
                    </>
                  )}
                </div>
              )}
              </FeatureGate>

              {/* Readiness status */}
              <div className="border-t border-border px-4 py-2.5">
                {ready ? (
                  <p className="text-xs text-green-600 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {t("supplier.readiness.readyText")}
                  </p>
                ) : (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    {t("supplier.readiness.warningText")}
                  </p>
                )}
              </div>
            </div>
          );
        })()}



        {/* Customer Messages */}
        {customerMessages.length > 0 && (() => {
          const unreadCount = customerMessages.filter((m) => !m.is_read).length;
          return (
            <div className="border border-border rounded-xl bg-muted/50 mb-4 overflow-hidden">
              <div
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-accent/30 transition-colors cursor-pointer"
                onClick={() => setMessagesOpen(!messagesOpen)}
              >
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">{t("supplierSettings.customerMessages")}</span>
                  {unreadCount > 0 && (
                    <span className="bg-primary text-primary-foreground text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                      {unreadCount}
                    </span>
                  )}
                </div>
                {messagesOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </div>
              {messagesOpen && (
                <div className="border-t border-border divide-y divide-border">
                  {customerMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`px-4 py-3 text-sm cursor-pointer hover:bg-accent/20 transition-colors ${!msg.is_read ? "bg-primary/5" : ""}`}
                      onClick={() => !msg.is_read && markMessageRead(msg.id)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-foreground flex items-center gap-1.5">
                          {!msg.is_read && <span className="w-1.5 h-1.5 bg-primary rounded-full inline-block" />}
                          {msg.customer_email}
                        </span>
                        <span className="text-xs text-muted-foreground">{new Date(msg.created_at).toLocaleDateString(lang)}</span>
                      </div>
                      <p className="text-muted-foreground whitespace-pre-wrap">
                        {translatedMessages[msg.id] || msg.message}
                      </p>
                      {msg.lang && msg.lang !== lang && (
                        <div className="mt-1">
                          {translatedMessages[msg.id] ? (
                            <button
                              className="text-[11px] text-muted-foreground/70 hover:text-muted-foreground underline"
                              onClick={(e) => { e.stopPropagation(); setTranslatedMessages((prev) => { const n = { ...prev }; delete n[msg.id]; return n; }); }}
                            >
                              {t("supplierSettings.showOriginal")}
                            </button>
                          ) : (
                            <button
                              className="text-[11px] text-primary/70 hover:text-primary underline"
                              disabled={translatingIds.has(msg.id)}
                              onClick={(e) => { e.stopPropagation(); translateMessage(msg.id, msg.message, msg.lang!); }}
                            >
                              {translatingIds.has(msg.id) ? "…" : t("supplierSettings.translate")}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Filter Panel — only for marketplace/all tabs */}
        {sourceTab !== "chatbot" && (
        <div className="border border-border rounded-xl bg-muted/50 mb-4 overflow-hidden">
          <div
            className="w-full flex flex-col px-4 py-3 hover:bg-accent/30 transition-colors cursor-pointer"
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">{t("filter.title")}</span>
                {filtersLocked && (
                  <span className="flex items-center gap-1 bg-primary/10 text-primary text-[10px] font-semibold px-2 py-0.5 rounded-full">
                    <Lock className="w-3 h-3" />
                    {t("filter.locked")}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                {(filterWorkType !== "all" || filterLocations.length > 0 || filterPriceFrom || filterPriceTo || filterDeadlinePreset) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      resetFilters();
                    }}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                  >
                    <RotateCcw className="w-3 h-3" />
                    {t("filter.reset")}
                  </button>
                )}
                {filtersOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </div>
            </div>
            {/* Summary of active filters when collapsed */}
            {!filtersOpen && (filterWorkType !== "all" || filterLocations.length > 0 || filterPriceFrom || filterPriceTo || filterDeadlinePreset) && (
              <div className="flex flex-wrap gap-1.5 mt-2 text-left">
                {filterWorkType !== "all" && (
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{isFacadeServiceSlug(filterWorkType) ? FACADE_SERVICE_LABEL : filterWorkType}</span>
                )}
                {filterLocations.length > 0 && (
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                    {filterLocations.some(l => l.is_whole_country) ? t("filter.wholeCountry") : filterLocations.map(l => l.label.split(",")[0]).join(", ")}
                  </span>
                )}
                {(filterPriceFrom || filterPriceTo) && (
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                    {filterPriceFrom || "0"} – {filterPriceTo || "∞"} Kč
                  </span>
                )}
                {filterDeadlinePreset && (
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{filterDeadlinePreset}</span>
                )}
              </div>
            )}
          </div>

          {filtersOpen && (
            <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
              {filtersLocked && (
                <div className="flex items-start gap-2 bg-accent rounded-lg px-3 py-2.5">
                  <Bell className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-muted-foreground">{t("filter.lockedHint")}</p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Work type */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{t("filter.workType")}</label>
                  <select
                    value={filterWorkType}
                    onChange={(e) => setFilterWorkType(e.target.value)}
                    disabled={filtersLocked}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                  >
                    <option value="all">{t("filter.allTypes")}</option>
                    {(supplier?.enabled_services || []).map((slug) => (
                      <option key={slug} value={slug}>{isFacadeServiceSlug(slug) ? FACADE_SERVICE_LABEL : slug}</option>
                    ))}
                  </select>
                </div>

                {/* Location - radius picker */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{t("filter.locationAreas")}</label>
                  <LocationRadiusPicker
                    value={filterLocations}
                    onChange={(locs) => {
                      setFilterLocations(locs);
                      setFilterLocationsDirty(true);
                    }}
                    disabled={filtersLocked}
                  />
                </div>

                {/* Price range */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{t("filter.priceFrom")}</label>
                  <input
                    type="number" min="0"
                    value={filterPriceFrom}
                    onChange={(e) => setFilterPriceFrom(e.target.value)}
                    disabled={filtersLocked}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                    placeholder="0 Kč"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{t("filter.priceTo")}</label>
                  <input
                    type="number" min="0"
                    value={filterPriceTo}
                    onChange={(e) => setFilterPriceTo(e.target.value)}
                    disabled={filtersLocked}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                    placeholder="∞ Kč"
                  />
                </div>

                {/* Deadline - month picker */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{t("filter.deadline")}</label>
                  <MonthPicker
                    value={filterDeadlinePreset}
                    onChange={setFilterDeadlinePreset}
                    disabled={filtersLocked}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={() => setFiltersOpen(false)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-border text-foreground rounded-lg hover:bg-accent transition-colors"
                >
                  <Filter className="w-3.5 h-3.5" />
                  {t("filter.apply")}
                </button>
                <button
                  onClick={async () => {
                    const newLocked = !filtersLocked;
                    setFiltersLocked(newLocked);
                    await Promise.all([
                      saveLocationPreferences(),
                      saveFilterState(newLocked),
                    ]);
                    if (newLocked) {
                      toast.success(t("filter.locationSaved"));
                      setFiltersOpen(false);
                    }
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    filtersLocked
                      ? "bg-primary text-primary-foreground"
                      : "border border-border text-foreground hover:bg-accent"
                  }`}
                >
                  {filtersLocked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                  {filtersLocked ? t("filter.unlock") : t("filter.save")}
                </button>
                <button
                  onClick={resetFilters}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-border text-muted-foreground rounded-lg hover:bg-accent hover:text-foreground transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  {t("filter.reset")}
                </button>
              </div>
            </div>
          )}
        </div>
        )}



        {/* Source tabs */}
        <div className="flex border-b border-border mb-4">
          <button
            onClick={() => setSourceTab("chatbot")}
            className={`px-5 py-2.5 text-sm font-medium transition-colors relative ${
              sourceTab === "chatbot" ? "text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t("supplier.ownChatbot")}
            {sourceTab === "chatbot" && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-foreground rounded-full" />
            )}
          </button>
          {hasMarketplace && (
            <button
              onClick={() => setSourceTab("marketplace")}
              className={`px-5 py-2.5 text-sm font-medium transition-colors relative ${
                sourceTab === "marketplace" ? "text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("supplier.marketplaceInquiries")}
              {sourceTab === "marketplace" && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-foreground rounded-full" />
              )}
            </button>
          )}
        </div>

        {/* Chatbot sub-filters */}
        {sourceTab === "chatbot" && (
          <div className="flex gap-1.5 mb-4">
            {([
              { key: "novy" as const, label: t("supplier.filterNew"), color: "bg-blue-100 text-blue-700" },
              { key: "nabidka" as const, label: t("supplier.filterQuoteSent"), color: "bg-green-100 text-green-700" },
              { key: "skryto" as const, label: t("supplier.filterHidden"), color: "bg-gray-100 text-gray-500" },
            ]).map(({ key, label, color }) => (
              <button
                key={key}
                onClick={() => setChatbotFilter(key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  chatbotFilter === key ? color : "bg-muted text-muted-foreground hover:bg-accent"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {(() => {
          const visibleInquiries = filteredInquiries.filter((inq) => {
            if (sourceTab === "chatbot") {
              if (inq.source !== "tenant_chat") return false;
              const isHidden = hiddenInquiries.has(inq.id);
              const hasSentQuote = submittedQuotes.has(inq.id);
              if (chatbotFilter === "novy") return !isHidden && !hasSentQuote;
              if (chatbotFilter === "nabidka") return !isHidden && hasSentQuote;
              if (chatbotFilter === "skryto") return isHidden;
              return true;
            }
            if (sourceTab === "marketplace") return inq.source !== "tenant_chat";
            return true;
          });

          if (visibleInquiries.length === 0) {
            return (
              <div className="text-center py-16">
                <FileText className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">{inquiries.length === 0 ? t("supplier.noInquiries") : t("filter.noResults")}</p>
                <p className="text-muted-foreground/60 text-xs mt-1">{inquiries.length === 0 ? t("supplier.noInquiriesHint") : t("filter.noResultsHint")}</p>
              </div>
            );
          }

          return (
            <div className="space-y-2">
              {visibleInquiries.map((inq) => {
                const details = parseDescription(inq.description);
                const sent = isQuoteSent(inq.id);
                const approved = isApproved(inq.id);
                return (
                  <div
                    key={inq.id}
                    className="border border-border rounded-xl p-4 bg-card hover:bg-accent/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/supplier/inquiry/${inq.id}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-medium text-foreground text-sm">{inq.title}</h3>
                          {inq.source === "tenant_chat" ? (
                            <span className="bg-violet-100 text-violet-700 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                              {t("supplier.chatbotBadge")}
                            </span>
                          ) : (
                            <span className="bg-gray-100 text-gray-600 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                              {t("supplier.marketplaceBadge")}
                            </span>
                          )}
                          {approved && (
                            <span className="bg-green-100 text-green-700 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                              {t("supplier.approved")}
                            </span>
                          )}
                          {sent && !approved && (
                            <span className="bg-primary/10 text-primary text-[10px] font-semibold px-2 py-0.5 rounded-full">
                              {t("supplier.quoteSent")}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                          {details["Plocha"] && (
                            <span className="flex items-center gap-1">
                              <Ruler className="w-3 h-3" />
                              {details["Plocha"]}
                            </span>
                          )}
                          {inq.region && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {inq.region}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(inq.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-3 flex-shrink-0 flex flex-col gap-1.5">
                        {approved ? (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 border border-green-200 rounded-lg">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            {t("supplier.approved")}
                          </span>
                        ) : sent ? (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded-lg">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            {t("supplier.quoteSent")}
                          </span>
                        ) : (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/supplier/inquiry/${inq.id}?tab=material`); }}
                              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-opacity hover:opacity-90 ${
                                materialSent.has(inq.id)
                                  ? "border border-border text-muted-foreground"
                                  : "bg-foreground text-background"
                              }`}
                            >
                              <Package className="w-3.5 h-3.5" />
                              {t("supplier.requestMaterial")}
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/supplier/inquiry/${inq.id}?tab=quote`); }}
                              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-opacity hover:opacity-90 ${
                                materialSent.has(inq.id)
                                  ? "bg-primary text-primary-foreground"
                                  : "border border-border text-muted-foreground"
                              }`}
                            >
                              <Send className="w-3.5 h-3.5" />
                              {t("supplier.quoteAction")}
                            </button>
                          </>
                        )}
                        {inq.source === "tenant_chat" && (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleHideInquiry(inq.id); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border text-muted-foreground hover:bg-accent transition-colors"
                          >
                            {hiddenInquiries.has(inq.id) ? (
                              <><Eye className="w-3.5 h-3.5" /> {t("supplier.restore")}</>
                            ) : (
                              <><EyeOff className="w-3.5 h-3.5" /> {t("supplier.hideAction")}</>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Marketplace upsell for free suppliers */}
        {!hasMarketplace && (
          <div className="relative rounded-lg border border-dashed border-gray-300 bg-gray-50/50 p-6 text-center mt-4">
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                <Lock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">{t("supplier.marketplaceUpsellTitle")}</p>
                <p className="mt-1 text-xs text-gray-500">
                  {t("supplier.marketplaceUpsellDescription")}
                </p>
              </div>
              <div className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-200">
                <Crown className="h-3 w-3" />
                {t("supplier.premiumLabel")}
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
      </div>
    </div>
  );
}
